import React, { Suspense, lazy } from 'react';
import { Switch, Route } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getStore } from './utils/utils';
import {
  SHOPPING_CART,
  CHECKOUT,
  QUICK_ORDER,
  NOT_FOUND,
  ORDER_CONFIRMATION,
  QUOTE_CONFIRMATION,
  CREATE_QUOTE,
  REVIEW_CHECKOUT,
  stripePaymentAppearance,
} from './global/constants';
import {
  getStripeCardTypes,
  getSpPublickey,
  getClientSecret,
  getCustomerSessionClientSecret,
} from './utils/extraUtils';
import { createSetupIntentId } from './global/actions';

const ShoppingCart = lazy(() => import('./containers/templates/ShoppingCart'));
const NotFound = lazy(() => import('./containers/templates/NotFound'));
const Checkout = lazy(() => import('./containers/templates/Checkout'));
const QuickOrder = lazy(() => import('./containers/templates/QuickOrder'));
const Confirmation = lazy(() => import('./containers/templates/Confirmation'));
const Quote = lazy(() => import('./containers/templates/Quote'));

const Switcher = ({ createSetupIntentId: createSetupIntent, globalData }) => {
  const store = getStore();
  const SP_PUBLIC_KEY = getSpPublickey(store);
  const stripeCardTypes = getStripeCardTypes(store);
  const pathSegments = window.location.pathname.split('/');
  const selectedLanguage = pathSegments[pathSegments.length - 2];
  const SP_PUBLIC_KEY_AVAILABLE = SP_PUBLIC_KEY !== undefined && SP_PUBLIC_KEY !== '';
  const isStripePaymentConfigAvailable = SP_PUBLIC_KEY_AVAILABLE && stripeCardTypes?.length > 0;
  React.useEffect(() => {
    const fetchSetupIntent = async () => {
      if (
        window.location.pathname.includes('checkout') &&
        !window.location.pathname.includes('checkout-review') &&
        isStripePaymentConfigAvailable
      ) {
        const { userId, spCustomerId, localeCountry } = JSON.parse(
          sessionStorage.getItem('waters.userDetails')
        );
        if (SP_PUBLIC_KEY && userId != null && localeCountry != null) {
          return new Promise((resolve, reject) => {
            createSetupIntent({
              userId,
              spCustomerId,
              countryCode: localeCountry,
              callback: success => {
                if (success) {
                  resolve();
                } else {
                  reject(new Error('Failed to create setup intent'));
                }
              },
            });
          });
        }
      }
      return null;
    };
    fetchSetupIntent();
  }, [
    window.location.pathname,
    isStripePaymentConfigAvailable,
    SP_PUBLIC_KEY,
    createSetupIntentId,
  ]);
  const clientSecretSwitcher = getClientSecret(globalData);
  const customerSessionClientSecretSwitcher = getCustomerSessionClientSecret(globalData);
  const stripeConfig = SP_PUBLIC_KEY && {
    spPublicKey: SP_PUBLIC_KEY,
    options: {
      allowedCardBrands: stripeCardTypes.replace(/\s/g, '').split(','),
      appearance: stripePaymentAppearance,
      clientSecret: clientSecretSwitcher,
      customerSessionClientSecret: customerSessionClientSecretSwitcher,
      locale: selectedLanguage,
    },
  };
  const stripePromise =
    SP_PUBLIC_KEY &&
    loadStripe(stripeConfig.spPublicKey, {
      betas: ['blocked_card_brands_beta_2'],
    });
  return (
    <Suspense fallback={<div />}>
      <Switch>
        <Route exact path="/">
          <ShoppingCart isEmpty />
        </Route>
        <Route exact path={SHOPPING_CART}>
          <ShoppingCart />
        </Route>
        <Route exact path={CHECKOUT}>
          {clientSecretSwitcher && SP_PUBLIC_KEY && stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={stripeConfig?.options}
              key={`${SP_PUBLIC_KEY}-${clientSecretSwitcher}`}
            >
              <Checkout pageContext="checkout" />
            </Elements>
          ) : (
            <Checkout pageContext="checkout" />
          )}
        </Route>
        <Route exact path={REVIEW_CHECKOUT}>
          <Checkout pageContext="checkout-review" />
        </Route>
        <Route exact path={QUICK_ORDER}>
          <QuickOrder />
        </Route>
        <Route exact path={NOT_FOUND}>
          <NotFound />
        </Route>
        <Route exact path={ORDER_CONFIRMATION}>
          <Confirmation pageContext="order" />
        </Route>
        <Route exact path={QUOTE_CONFIRMATION}>
          <Confirmation pageContext="quote" />
        </Route>
        <Route exact path={CREATE_QUOTE}>
          <Quote pageContext="quote" />
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Suspense>
  );
};

Switcher.propTypes = {
  createSetupIntentId: PropTypes.func.isRequired,
  globalData: PropTypes.shape({
    stripeData: PropTypes.shape({
      setupIntentId: PropTypes.string,
      customerSessionId: PropTypes.string,
    }),
  }),
};

Switcher.defaultProps = {
  globalData: {},
};
const mapDispatchToProps = {
  createSetupIntentId,
};

const mapStateToProps = state => ({
  globalData: state.global.globalData,
});

export default connect(mapStateToProps, mapDispatchToProps)(Switcher);
